import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, Pencil, Plus, Trash2, UserRound } from 'lucide-react';
import { listSalesmen, createSalesman, updateSalesman, deleteSalesman } from '../api/resources';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import DataTable from '../components/ui/DataTable';
import Modal from '../components/ui/Modal';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import PageHeader from '../components/ui/PageHeader';

/**
 * The people who bring the work in. Small master list, kept in one place
 * rather than created in passing from the order screen — a name typed in a
 * hurry there became a second "Ravi" nobody could tidy up afterwards.
 */
export default function Salesmen() {
  const qc = useQueryClient();
  const { role } = useAuth();
  const { success, error } = useToast();
  const isAdmin = role === 'ADMIN';
  const [form, setForm] = useState(null); // { id?, name, contact }
  const [del, setDel] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['salesmen', 'all'],
    queryFn: () => listSalesmen({ all: 1 }),
  });
  const rows = data?.salesmen || [];
  const invalidate = () => qc.invalidateQueries({ queryKey: ['salesmen'] });

  const saveMut = useMutation({
    mutationFn: (body) => (body.id ? updateSalesman(body.id, body) : createSalesman(body)),
    onSuccess: (d) => { success(d.message || 'Saved.'); setForm(null); invalidate(); },
    onError: (e) => error(e.response?.data?.message || 'Could not save this salesman.'),
  });

  const delMut = useMutation({
    mutationFn: deleteSalesman,
    onSuccess: (d) => { success(d.message || 'Removed.'); setDel(null); invalidate(); },
    onError: (e) => { error(e.response?.data?.message || 'Could not remove this salesman.'); setDel(null); },
  });

  const columns = [
    {
      key: 'name',
      header: 'Salesman',
      hideable: false,
      render: (s) => (
        <span className="flex items-center gap-2">
          <span className="chip-brand flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold">
            {(s.name || '?').trim().charAt(0).toUpperCase()}
          </span>
          <span className="font-medium text-content">{s.name}</span>
        </span>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      render: (s) => (s.contact
        ? <a href={`tel:${s.contact}`} className="font-medium text-brand-600 hover:underline">{s.contact}</a>
        : <span className="text-content-subtle">—</span>),
    },
    {
      key: 'status',
      header: 'Status',
      render: (s) => (s.status === 'INACTIVE'
        ? <span className="badge bg-surface-hover text-content-muted">Left</span>
        : <span className="badge bg-success-50 text-success-700">Working</span>),
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      hideable: false,
      align: 'right',
      exportValue: () => '',
      render: (s) => (
        <span className="flex items-center justify-end gap-0.5">
          {s.status === 'INACTIVE' && isAdmin && (
            <button
              className="btn-icon hover:bg-success-50 hover:text-success-700"
              title="Bring them back"
              onClick={() => saveMut.mutate({ name: s.name, contact: s.contact })}
            >
              <BadgeCheck size={15} />
            </button>
          )}
          <button className="btn-icon" title="Edit" disabled={!isAdmin} onClick={() => setForm(s)}><Pencil size={15} /></button>
          <button
            className="btn-icon hover:bg-danger-50 hover:text-danger-600"
            title="Remove"
            disabled={!isAdmin}
            onClick={() => setDel(s)}
          >
            <Trash2 size={15} />
          </button>
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        icon={UserRound}
        title="Salesmen"
        subtitle="The people credited on orders — add them here, pick them on the order"
        actions={(
          <button className="btn-primary" onClick={() => setForm({ name: '', contact: '' })}>
            <Plus size={15} /> Add Salesman
          </button>
        )}
      />

      <DataTable
        columns={columns}
        rows={rows}
        loading={isLoading}
        storageKey="salesmen"
        searchPlaceholder="Search by name or contact…"
        searchKeys={['name', 'contact']}
        exportable
        exportFilename="salesmen"
        emptyTitle="No salesmen yet"
        emptyHint="Add the people who bring work in, then pick them on an order."
      />

      <Modal open={!!form} onClose={() => setForm(null)} title={form?.id ? 'Edit Salesman' : 'Add Salesman'} size="sm">
        {form && (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (!form.name.trim()) return error('A name is needed.');
              saveMut.mutate({ ...form, name: form.name.trim(), contact: form.contact?.trim() || null });
            }}
          >
            <label className="block">
              <span className="label">Name</span>
              <input
                className="input"
                autoFocus
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Ravi Kumar"
              />
            </label>
            <label className="block">
              <span className="label">Contact <span className="font-normal text-content-subtle">(optional)</span></span>
              <input
                className="input"
                value={form.contact || ''}
                onChange={(e) => setForm((f) => ({ ...f, contact: e.target.value }))}
                placeholder="Mobile or extension"
              />
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" className="btn-outline" onClick={() => setForm(null)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={saveMut.isPending}>
                {saveMut.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      <ConfirmDialog
        open={!!del}
        onClose={() => setDel(null)}
        title="Remove salesman"
        message={del
          ? `Remove "${del.name}"? If their name is on past orders they are kept and marked as left, so that history stays correct.`
          : ''}
        busy={delMut.isPending}
        onConfirm={() => delMut.mutate(del.id)}
      />
    </div>
  );
}
